import { useCallback, useMemo } from 'react';
import { Task, FullProject, LogEntry, ProjectMetadata } from '../../types';
import { useTaskCRUD } from './useTaskCRUD';
import { useTaskPositioning } from './useTaskPositioning';
import { useTaskIdeaAndAI } from './useTaskIdeaAndAI';
import { createProjectSyncTracker, ProjectSyncTracker } from './sync/projectSyncStateTracker';

export interface UseTaskOperationsProps {
  allProjects: FullProject[];
  syncTracker?: ProjectSyncTracker;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  rawProjectsRef?: React.MutableRefObject<FullProject[]>;
  rawTasksRef?: React.MutableRefObject<Task[]>;
  ownedTasksRef?: React.MutableRefObject<Task[]>;
  migratedProjectIdsRef: React.MutableRefObject<Set<string>>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  currentProjectIdRef: React.MutableRefObject<string>;
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  saveSnapshot: () => void;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  markProjectDirty: (id: string) => void;
  markTaskDirty?: (taskId: string | number, projectId?: string) => void;
  dirtyTaskIdsRef?: React.MutableRefObject<Set<string>>;
  recentlyDeletedTaskIdsRef?: React.MutableRefObject<Set<string>>;
  syncProjectToFirestore: (project: FullProject, isBackground?: boolean) => Promise<boolean | void | number | null>;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
  getProjectTasksWithCommVorgaenge?: (proj: FullProject, projectsList: FullProject[]) => Task[];
}

/**
 * Facade hook orchestrating task operations across:
 * - useTaskCRUD (Creation, updates, deletion, saving, duplication, metadata)
 * - useTaskPositioning (Drag-and-drop reordering, coordinates, date sorting, sortOrder migration)
 * - useTaskIdeaAndAI (Promotion of ideas to tasks, AI plan decomposition and import)
 */
export function useTaskOperations({
  allProjects,
  syncTracker: propSyncTracker,
  allProjectsRef,
  rawProjectsRef,
  rawTasksRef,
  ownedTasksRef,
  migratedProjectIdsRef,
  setAllProjects,
  currentProjectId,
  currentProjectIdRef,
  tasks,
  setTasks,
  setProjectMetadata,
  saveSnapshot,
  addLogEntry,
  markProjectDirty,
  markTaskDirty,
  dirtyTaskIdsRef,
  recentlyDeletedTaskIdsRef,
  syncProjectToFirestore,
  user,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  normalizeProjectId,
  getProjectTasksWithCommVorgaenge
}: UseTaskOperationsProps) {

  const syncTracker = useMemo(
    () =>
      propSyncTracker ??
      createProjectSyncTracker({
        allProjectsRef,
        rawProjectsRef,
        rawTasksRef,
        ownedTasksRef,
        dirtyTaskIdsRef,
        recentlyDeletedTaskIdsRef,
      }),
    [propSyncTracker, allProjectsRef, rawProjectsRef, rawTasksRef, ownedTasksRef, dirtyTaskIdsRef, recentlyDeletedTaskIdsRef]
  );

  const onMarkTaskDirty = useCallback((taskId: string | number, pId?: string) => {
    if (markTaskDirty) {
      markTaskDirty(taskId, pId);
    } else if (pId) {
      markProjectDirty(pId);
    }
  }, [markTaskDirty, markProjectDirty]);

  // 1. Task CRUD operations
  const {
    commitTasks,
    updateTask,
    updateTasksMultiple,
    updateProjectMetadata,
    updateProjectCategory,
    deleteTask,
    handleSaveTask,
    addTask,
    duplicateTask
  } = useTaskCRUD({
    allProjects,
    syncTracker,
    allProjectsRef,
    migratedProjectIdsRef,
    setAllProjects,
    currentProjectId,
    currentProjectIdRef,
    tasks,
    setTasks,
    setProjectMetadata,
    saveSnapshot,
    addLogEntry,
    markProjectDirty,
    onMarkTaskDirty,
    syncProjectToFirestore,
    user,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
    getProjectTasksWithCommVorgaenge
  });

  // 2. Task positioning, reordering, and sorting
  const {
    batchUpdateTaskPositions,
    sortTasksByDate,
    migrateProjectToSortOrder,
    reorderTask
  } = useTaskPositioning({
    syncTracker,
    allProjectsRef,
    migratedProjectIdsRef,
    setAllProjects,
    currentProjectId,
    tasks,
    setTasks,
    saveSnapshot,
    markProjectDirty,
    onMarkTaskDirty,
    syncProjectToFirestore,
    user,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    commitTasks,
    getProjectTasksWithCommVorgaenge
  });

  // 3. Task ideas and AI planning
  const {
    upgradeIdeaToTask,
    handleAddAITasks
  } = useTaskIdeaAndAI({
    setAllProjects,
    currentProjectId,
    setTasks,
    setProjectMetadata,
    saveSnapshot,
    addLogEntry,
    markProjectDirty,
    onMarkTaskDirty,
    user,
    COMM_PROJECT_ID
  });

  return {
    commitTasks,
    updateTask,
    updateTasksMultiple,
    deleteTask,
    handleSaveTask,
    addTask,
    upgradeIdeaToTask,
    duplicateTask,
    batchUpdateTaskPositions,
    sortTasksByDate,
    handleAddAITasks,
    migrateProjectToSortOrder,
    updateProjectMetadata,
    updateProjectCategory,
    reorderTask
  };
}
